import React, { useContext, useEffect, useState } from 'react';
import { Nav } from 'react-bootstrap';
import Logo from '../assets/Logo.png';
import { NavLink } from './NavLink';
import {
    faArchive,
    faBug,
    faCar,
    faClipboardList,
    faCogs,
    faCreditCard,
    faExclamationTriangle,
    faFileInvoiceDollar,
    faHistory,
    faPrint,
    faShoppingBag,
    faSprayCan,
    faTasks,
    faToiletPaper,
    faUsers,
    faUserTie,
    faWarehouse,
} from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { useTranslation } from 'react-i18next';
import { useLocation } from 'react-router-dom';
import { AuthContext } from '../context/AuthContext';
import { RequiredPermissions } from './RequiredPermissions';
import { Role } from '../utils/Enums/Roles';
import { getHandoutRequestsRevision } from '../utils/warehouses-hooks';
import {
    getPendingInventoryRevisionDistributorIds,
    inventoryRevisionObservationsReadEvent,
} from '../utils/clients-hooks';

export const Navigation = () => {
    const { t } = useTranslation('translation', { keyPrefix: 'common' });
    const location = useLocation();
    const { hasPermission, permissions } = useContext(AuthContext);
    const [hasHandoutRequests, setHasHandoutRequests] = useState(false);
    const [hasPendingInventoryRevisions, setHasPendingInventoryRevisions] = useState(false);
    const canViewHandoutRequests = hasPermission([Role.ADMIN, Role.COORDINATOR, Role.WAREHOUSE, Role.FINANCIAL]);
    const canViewInventoryRevisions = hasPermission([Role.ADMIN, Role.WAREHOUSE, Role.COORDINATOR, Role.FINANCIAL]);

    useEffect(() => {
        let isActive = true;
        let pollingInterval: ReturnType<typeof setInterval>;

        if (!canViewHandoutRequests) {
            setHasHandoutRequests(false);
            return;
        }

        const refreshHandoutRequestsIndicator = () => {
            // Only requests awaiting preparation should light up the warning triangle.
            getHandoutRequestsRevision({filter: {tags: [], search: '', state: 'PENDING'}})
                .then(({ totalCount }) => {
                    if (!isActive) return;

                    setHasHandoutRequests(totalCount > 0);
                })
                .catch(() => {
                    if (isActive) {
                        setHasHandoutRequests(false);
                    }
                });
        };

        refreshHandoutRequestsIndicator();
        pollingInterval = setInterval(refreshHandoutRequestsIndicator, 30000);

        return () => {
            isActive = false;
            clearInterval(pollingInterval);
        };
    }, [canViewHandoutRequests, permissions]);

    useEffect(() => {
        let isActive = true;
        let pollingInterval: ReturnType<typeof setInterval>;

        if (!canViewInventoryRevisions) {
            setHasPendingInventoryRevisions(false);
            return;
        }

        const refreshInventoryRevisionsIndicator = () => {
            getPendingInventoryRevisionDistributorIds(true)
                .then((distributorIds) => {
                    if (!isActive) return;

                    setHasPendingInventoryRevisions(distributorIds.length > 0);
                })
                .catch(() => {
                    if (isActive) {
                        setHasPendingInventoryRevisions(false);
                    }
                });
        };

        refreshInventoryRevisionsIndicator();
        window.addEventListener(inventoryRevisionObservationsReadEvent, refreshInventoryRevisionsIndicator);
        pollingInterval = setInterval(refreshInventoryRevisionsIndicator, 120000);

        return () => {
            isActive = false;
            window.removeEventListener(inventoryRevisionObservationsReadEvent, refreshInventoryRevisionsIndicator);
            clearInterval(pollingInterval);
        };
    }, [canViewInventoryRevisions, permissions]);

    const handoutRequestsIndicator = hasHandoutRequests ? (
        <FontAwesomeIcon
            className='text-warning ms-1 warning-triangle-bordered handout-requests-indicator-pulse'
            icon={faExclamationTriangle}
            title={t('handoutRequestsAvailable')}
        />
    ) : undefined;
    const revisionsIndicator = hasPendingInventoryRevisions && location.pathname !== '/revisions' ? (
        <FontAwesomeIcon
            className='text-warning ms-1 warning-triangle-bordered handout-requests-indicator-pulse'
            icon={faExclamationTriangle}
            title={t('inventoryRevisionsAvailable')}
        />
    ) : undefined;

    return (
        <Nav
            defaultActiveKey='/home'
            className='nav-container flex-column position-relative align-items-center h-100 w-100 pt-2'
            role='tablist'
        >
            <div className='nav d-flex flex-column align-items-start w-100'>
                <div className='nav-logo w-100'>
                    <NavLink
                        path={hasPermission([Role.COORDINATOR, Role.INSTALLER, Role.DISTRIBUTOR]) ? '/dashboard' : '/'}
                        image={Logo}
                        label='Dashboard'
                    />
                </div>
                <h6 className='px-4 pt-3 text-uppercase'>{t('menu')}</h6>
                <div className='nav-menu-scroll w-100'>
                    <RequiredPermissions requiredPermissions={[Role.ADMIN, Role.COORDINATOR, Role.INSTALLER, Role.DISTRIBUTOR, Role.FINANCIAL]}>
                        <NavLink path='/dashboard' label={t('dashboard')} icon={faTasks}/>
                    </RequiredPermissions>
                    <RequiredPermissions requiredPermissions={[Role.ADMIN, Role.COORDINATOR, Role.WAREHOUSE, Role.FINANCIAL]}>
                        <NavLink path='/passports' label={t('passports')} icon={faSprayCan}/>
                    </RequiredPermissions>
                    <RequiredPermissions requiredPermissions={[Role.ADMIN, Role.COORDINATOR, Role.WAREHOUSE, Role.FINANCIAL]}>
                        <NavLink path='/warehouses' label={t('warehouses')} icon={faWarehouse}/>
                    </RequiredPermissions>
                    <RequiredPermissions requiredPermissions={[Role.ADMIN, Role.COORDINATOR, Role.WAREHOUSE, Role.FINANCIAL]}>
                        <NavLink
                            path='/handout-requests'
                            label={t('handoutRequests')}
                            icon={faClipboardList}
                            indicator={handoutRequestsIndicator}
                        />
                    </RequiredPermissions>
                    <RequiredPermissions requiredPermissions={[Role.INSTALLER, Role.DISTRIBUTOR]} forbiddenAccess={[Role.ADMIN]}>
                        <NavLink path='/inventory' label={t('inventory')} icon={faShoppingBag}/>
                    </RequiredPermissions>
                    <RequiredPermissions requiredPermissions={[Role.ADMIN]}>
                        <NavLink path='/users' label={t('users')} icon={faUserTie}/>
                    </RequiredPermissions>
                    <RequiredPermissions requiredPermissions={[Role.ADMIN]}>
                        <NavLink path='/fleet' label={t('fleet')} icon={faCar}/>
                    </RequiredPermissions>
                    <RequiredPermissions requiredPermissions={[Role.ADMIN]}>
                        <NavLink path='/fiscalDevices' label={t('fiscalDevices')} icon={faPrint}/>
                    </RequiredPermissions>
                    <RequiredPermissions requiredPermissions={[Role.ADMIN, Role.COORDINATOR]}>
                        <NavLink path='/groups' label={t('groups')} icon={faUsers}/>
                    </RequiredPermissions>
                    <RequiredPermissions requiredPermissions={[Role.ADMIN, Role.COORDINATOR, Role.DISTRIBUTOR, Role.INSTALLER]}>
                        <NavLink path='/tasks' label={t('tasks')} icon={faTasks}/>
                    </RequiredPermissions>
                    <RequiredPermissions requiredPermissions={[Role.ADMIN, Role.COORDINATOR, Role.INSTALLER, Role.DISTRIBUTOR, Role.FINANCIAL]}>
                        <NavLink path='/customers' label={t('customers')} icon={faUserTie}/>
                    </RequiredPermissions>
                    <RequiredPermissions requiredPermissions={[Role.ADMIN, Role.COORDINATOR, Role.FINANCIAL]}>
                        <NavLink path='/invoices' label={t('payments')} icon={faFileInvoiceDollar}/>
                    </RequiredPermissions>
                    <RequiredPermissions requiredPermissions={[Role.ADMIN, Role.WAREHOUSE, Role.COORDINATOR, Role.FINANCIAL]}>
                        <NavLink path='/references' label={t('references')} icon={faArchive}/>
                    </RequiredPermissions>
                    <RequiredPermissions requiredPermissions={[Role.ADMIN, Role.WAREHOUSE, Role.COORDINATOR, Role.FINANCIAL]}>
                        <NavLink path='/protocols' label={t('protocols')} icon={faToiletPaper}/>
                    </RequiredPermissions>
                    <RequiredPermissions requiredPermissions={[Role.ADMIN, Role.WAREHOUSE, Role.COORDINATOR, Role.FINANCIAL]}>
                        <NavLink
                            path='/revisions'
                            label={t('revisions')}
                            icon={faClipboardList}
                            indicator={revisionsIndicator}
                        />
                    </RequiredPermissions>
                    <RequiredPermissions requiredPermissions={[Role.ADMIN]}>
                        <NavLink path='/logs' label={t('logs')} icon={faHistory}/>
                    </RequiredPermissions>
                    <RequiredPermissions requiredPermissions={[Role.ADMIN]}>
                        <NavLink path='/options' label={t('options')} icon={faCogs}/>
                    </RequiredPermissions>
                    {process.env.NODE_ENV !== 'production' && (
                        <>
                            <RequiredPermissions requiredPermissions={[Role.ADMIN]}>
                                <NavLink path='/printer-attempts' label={t('printerAttempts')} icon={faPrint}/>
                            </RequiredPermissions>
                            <RequiredPermissions requiredPermissions={[Role.ADMIN]}>
                                <NavLink path='/mobile-errors' label={t('mobileErrors')} icon={faBug}/>
                            </RequiredPermissions>
                            <RequiredPermissions requiredPermissions={[Role.ADMIN]}>
                                <NavLink path='/card-attempts' label={t('cardAttempts')} icon={faCreditCard}/>
                            </RequiredPermissions>
                            <RequiredPermissions requiredPermissions={[Role.ADMIN]}>
                                <NavLink path='/stuck-payments' label='Stuck payments' icon={faExclamationTriangle}/>
                            </RequiredPermissions>
                            <RequiredPermissions requiredPermissions={[Role.ADMIN]}>
                                <NavLink path='/swagger' label={'Swagger'} icon={faCogs}/>
                            </RequiredPermissions>
                        </>
                    )}
                </div>
            </div>
        </Nav>
    );
};
